<template>
    <nav
        :aria-label="ariaLabel ?? t('reflections.nav.ariaLabel')"
        class="w-full flex flex-row gap-2 overflow-x-auto scrollbar-none lg:w-[190px] lg:shrink-0 lg:bg-surface-raised lg:border lg:border-border-strong lg:rounded-lg lg:p-2.5 lg:flex-col lg:gap-2.5 lg:overflow-visible">
        <BaseButton theme="tertiary" :class="[navItemBase, upcoming.key === active ? navItemActive : navItemHover]" @click="onSelect(upcoming.key)">
            <span class="scaffold-body-small text-ink">{{ upcoming.label }}</span>
        </BaseButton>
        <HorizontalDivider v-if="secondary.length" as="hr" spacing="none" class="hidden lg:block" />
        <BaseButton
            v-for="item in secondary"
            :key="item.key"
            theme="tertiary"
            :class="[navItemBase, item.key === active ? navItemActive : navItemHover]"
            @click="onSelect(item.key)">
            <span class="inline-block w-2 h-2 rounded-full shrink-0" :style="{ backgroundColor: item.dotColor }" aria-hidden="true"></span>
            <span class="scaffold-body-small text-ink">{{ item.label }}</span>
        </BaseButton>
        <template v-if="showAdmin">
            <HorizontalDivider as="hr" spacing="none" class="hidden lg:block" />
            <BaseButton theme="tertiary" :class="[navItemBase, 'admin' === active ? navItemActive : navItemHover]" @click="onSelect('admin')">
                <span class="scaffold-body-small text-ink">{{ t("reflections.nav.admin") }}</span>
            </BaseButton>
        </template>
    </nav>
</template>

<script lang="ts">
import type { ReflectionTabsConfig } from "~vue/api/generated/reflections";

export type ReflectionsTabKey = "upcoming" | "sixty" | "self" | "team" | "admin";

export interface NavItem {
    key: Exclude<ReflectionsTabKey, "admin">;
    label: string;
    dotColor?: string;
}

/** Return whether *key* is available to an organization with the given tab configuration. */
export function isTabAvailable(key: ReflectionsTabKey, tabs: ReflectionTabsConfig | undefined): boolean {
    switch (key) {
        case "sixty":
            return tabs?.show_360s ?? true;
        case "self":
            return tabs?.show_self_reflection ?? true;
        case "team":
            return tabs?.show_team_reflections ?? true;
        default:
            return true;
    }
}

interface Props {
    active: ReflectionsTabKey;
    ariaLabel?: string;
    showAdmin?: boolean;
    tabs?: ReflectionTabsConfig;
}
</script>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { active, ariaLabel, showAdmin = false, tabs } = defineProps<Props>();
const { t } = useI18n();
const emit = defineEmits<{ (event: "select", key: ReflectionsTabKey): void }>();

// Dot colors match the prototype's inline styles exactly. Kept as literals
// (not tokens) because the prototype treats these as a mini brand palette
// specific to the reflections nav, not part of the broader design system.
const upcoming = computed<NavItem>(() => ({ key: "upcoming", label: t("reflections.nav.upcoming") }));
const allSecondary = computed<NavItem[]>(() => [
    { key: "sixty", label: t("reflections.nav.sixty"), dotColor: "#e84c8d" },
    { key: "self", label: t("reflections.nav.self"), dotColor: "#FBB877" },
    { key: "team", label: t("reflections.nav.team"), dotColor: "#bee59f" },
]);
const secondary = computed(() => allSecondary.value.filter((item) => isTabAvailable(item.key, tabs)));

// On mobile the nav is a horizontal pill row (each item gets its own bordered
// pill so it's easy to tap). At lg+ it becomes the prototype's card sidebar
// where items share a container and the hairline separates Upcoming from the
// rest. Combining both keeps the DOM identical across breakpoints — only the
// classes change.
const navItemBase =
    "flex items-center gap-2 px-3 py-2 rounded-full bg-surface-raised border border-border-strong shrink-0 whitespace-nowrap text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-control-ring " +
    "lg:w-full lg:rounded-md lg:border-0 lg:px-2.5 lg:py-2.5 lg:whitespace-normal";
const navItemHover = "hover:bg-surface-hover";
const navItemActive = "bg-surface-sunken lg:bg-surface-sunken";

function onSelect(key: ReflectionsTabKey) {
    emit("select", key);
}
</script>

<style scoped>
/* Hide the horizontal scrollbar on mobile — the pill row is short enough that
   the bar is visual noise. Kept as scoped CSS since Tailwind doesn't have a
   built-in `scrollbar-none` utility for both WebKit and Firefox. */
.scrollbar-none {
    scrollbar-width: none;
}

.scrollbar-none::-webkit-scrollbar {
    display: none;
}
</style>
